// 자격증 데이터 동적 생성 및 관리 유틸리티

/**
 * 자격증 키에서 한글 제목을 추출하는 매핑
 */
export const examTitleMap = {
  'electrical-engineer': '전기기사',
  'electrical-industrial-engineer': '전기산업기사', 
  'electrical-construction-engineer': '전기공사기사',
  'electrical-technician': '전기기능사',
  'information-processing-engineer': '정보처리기사',
  'information-processing': '정보처리기사',
  'architectural-engineer': '건축기사',
  'construction-engineer': '건축기사',
  'civil-engineer': '토목기사',
  'mechanical-engineer': '기계기사',
  'fire-engineer': '소방설비기사',
  'electronics-engineer': '전자기사',
  'industrial-safety-engineer': '산업안전기사',
  'gas-engineer': '가스기사',
  'construction-machinery-engineer': '건설기계기사',
  'chemical-engineer': '화공기사',
  'environmental-engineer': '환경기사',
  'safety-engineer': '안전기사',
  'quality-engineer': '품질경영기사',
  'korean-cooking-technician': '한식조리기능사'
};

/**
 * 자격증 분야별 기본 과목 매핑
 */
export const examSubjectMap = {
  'electrical': ['전기이론', '전력공학', '전기기기', '전기설비', '전기법규'],
  'information': ['소프트웨어설계', '소프트웨어개발', '데이터베이스구축', '프로그래밍언어활용', '정보시스템구축관리'],
  'architectural': ['건축계획', '건축구조', '건축시공', '건축설비', '관계법규'],
  'civil': ['토목설계', '토목시공', '토목재료', '측량학', '관계법규'],
  'mechanical': ['기계설계', '기계가공', '재료역학', '열역학', '관계법규'],
  'fire': ['소방원론', '소방시설의 구조 및 원리', '소방관계 법규', '소방시설 설치 유지 및 안전관리', '위험물 안전관리'],
  'electronics': ['전자회로', '전자계측', '반도체공학', '통신이론', '관계법규'],
  'default': ['기본이론', '전공기초', '실무응용', '관계법규']
};

/**
 * 자격증 키에서 분야 추출
 */
export function getExamField(examKey) {
  if (examKey.includes('electrical')) return 'electrical';
  if (examKey.includes('information')) return 'information';
  if (examKey.includes('architectural') || examKey.includes('construction')) return 'architectural';
  if (examKey.includes('civil')) return 'civil';
  if (examKey.includes('mechanical')) return 'mechanical';
  if (examKey.includes('fire')) return 'fire';
  if (examKey.includes('electronics')) return 'electronics';
  return 'default';
}

/**
 * 자격증 등급별 기본 설정
 */
export const examGradeDefaults = {
  '기사': {
    totalQuestions: 100,
    timeLimit: 150,
    difficulty: 'medium',
    averagePassRate: 25.0,
    totalExaminees: 30000
  },
  '산업기사': {
    totalQuestions: 100,
    timeLimit: 150,
    difficulty: 'medium',
    averagePassRate: 28.0,
    totalExaminees: 25000
  },
  '기능사': {
    totalQuestions: 60,
    timeLimit: 120,
    difficulty: 'easy',
    averagePassRate: 40.0,
    totalExaminees: 20000
  },
  'default': {
    totalQuestions: 100,
    timeLimit: 150,
    difficulty: 'medium',
    averagePassRate: 30.0,
    totalExaminees: 15000
  }
};

/**
 * 자격증 키에서 등급 추출
 */
export function getExamGrade(examKey, title) {
  if (title.includes('기사') && !title.includes('산업')) return '기사';
  if (title.includes('산업기사')) return '산업기사';
  if (title.includes('기능사')) return '기능사';
  return 'default';
}

/**
 * 동적 기본 자격증 데이터 생성
 * @param {string} examKey - 자격증 키 (예: 'electrical-engineer')
 * @returns {Object} 생성된 자격증 데이터
 */
export function generateDefaultExamData(examKey) {
  // 제목 추출
  const title = examTitleMap[examKey] || examKey.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  // 분야별 과목 추출
  const field = getExamField(examKey);
  const subjects = examSubjectMap[field] || examSubjectMap.default;

  // 등급별 기본 설정
  const grade = getExamGrade(examKey, title);
  const gradeDefaults = examGradeDefaults[grade] || examGradeDefaults.default;

  return {
    title: title,
    description: `${title} 자격증 취득을 위한 필기시험`,
    totalQuestions: gradeDefaults.totalQuestions,
    timeLimit: gradeDefaults.timeLimit,
    passingScore: 60,
    popular: false,
    subjects: subjects,
    years: {
      2024: {
        sessions: { 1: { available: true }, 2: { available: true } },
        difficulty: gradeDefaults.difficulty,
        passRate: gradeDefaults.averagePassRate
      },
      2023: {
        sessions: { 1: { available: true }, 2: { available: true } },
        difficulty: gradeDefaults.difficulty, 
        passRate: gradeDefaults.averagePassRate - 1.5
      }
    },
    statistics: {
      totalExaminees: gradeDefaults.totalExaminees,
      averagePassRate: gradeDefaults.averagePassRate,
      popularSessions: ['2024년 1회', '2024년 2회', '2023년 2회']
    },
    studyTips: {
      studyPeriod: grade === '기능사' ? '2-4개월' : '3-6개월',
      keyPoints: [
        '기본 이론 충실한 학습',
        '기출문제 반복 풀이',
        '관련 법규 정확한 암기',
        '실무 적용 능력 배양'
      ]
    },
    careerInfo: {
      jobProspects: `${field === 'electrical' ? '전기' : field === 'information' ? 'IT' : field === 'architectural' ? '건축' : '관련'} 분야 전문 업무`,
      salary: grade === '기사' ? '연봉 3,200만원~4,800만원' : grade === '산업기사' ? '연봉 2,800만원~4,200만원' : '연봉 2,500만원~3,800만원',
      advancement: grade === '기능사' ? '산업기사, 기사' : grade === '산업기사' ? '기사' : '관련 상위 자격증'
    }
  };
}

/**
 * 현재 데이터에 없는 자격증들을 동적으로 추가
 * @param {Object} currentExamData - 현재 자격증 데이터
 * @param {Array} requiredExamKeys - 필요한 자격증 키 배열
 * @returns {Object} 업데이트된 자격증 데이터
 */
export function ensureExamData(currentExamData, requiredExamKeys) {
  const updatedData = { ...currentExamData };
  let hasUpdates = false;
  
  requiredExamKeys.forEach(examKey => {
    if (!updatedData[examKey]) {
      console.log(`자격증 "${examKey}" 데이터를 찾을 수 없어 기본 데이터를 생성합니다.`);
      updatedData[examKey] = generateDefaultExamData(examKey);
      hasUpdates = true;
    }
  });
  
  if (hasUpdates) {
    console.log('새로운 자격증 데이터가 동적으로 생성되었습니다.');
  }
  
  return updatedData;
}

/**
 * 자격증 제목으로 키 찾기
 * @param {string} title - 자격증 제목 (예: '전기기사')
 * @returns {string|null} 자격증 키 또는 null
 */
export function findExamKeyByTitle(title) {
  return Object.keys(examTitleMap).find(key => examTitleMap[key] === title) || null;
}